/**
 * Sales domain event schemas.
 *
 * Typed payload validation for all sales-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module sales.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const PipelineIntelligenceUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('sales'),
  type: z.literal('pipeline_intelligence_updated'),
  payload: z.object({
    opportunityId: z.string(),
    clientId: z.string(),
    engagementLevel: z.string().optional(),
    velocity: z.number().optional(),
    activities: z.array(z.record(z.unknown())).optional(),
    source: z.string().optional(),
  }),
})

export const ProposalSentEvent = BackboneEventSchema.extend({
  domain: z.literal('sales'),
  type: z.literal('proposal_sent'),
  payload: z.object({
    proposalId: z.string(),
    clientId: z.string(),
    opportunityId: z.string().optional(),
    amount: z.number().optional(),
    source: z.string(),
  }),
})

export const RevenueCollectedEvent = BackboneEventSchema.extend({
  domain: z.literal('sales'),
  type: z.literal('revenue_collected'),
  payload: z.object({
    invoiceId: z.string(),
    amount: z.number(),
    clientId: z.string(),
    opportunityId: z.string().optional(),
    source: z.string(),
  }),
})

export const PartialPaymentReceivedEvent = BackboneEventSchema.extend({
  domain: z.literal('sales'),
  type: z.literal('partial_payment_received'),
  payload: z.object({
    invoiceId: z.string(),
    paymentAmount: z.number(),
    remainingBalance: z.number().optional(),
    clientId: z.string(),
    source: z.string(),
  }),
})

// ── All sales events ────────────────────────────────────────────────────

export const SalesDomainEvents = [
  PipelineIntelligenceUpdatedEvent,
  ProposalSentEvent,
  RevenueCollectedEvent,
  PartialPaymentReceivedEvent,
] as const
